/**
 * 当月pt を「要件定義pt」と「開発pt」へ割る純関数。
 *
 * 2026-10 の開発フロー変更で要件定義は開発ptとは**別管理**になった（fibo予算 `_PDG_v2` が
 * 「要件定義」と「開発pt（要件定義を除く）」を別行で持つ）。KPI 画面の
 * 「開発pt (MTD)」カードは全体 / チーム単位で同じ切り出しをしているが、**メンバー個人の
 * 当月pt にはその内訳が無かった**。ここはその個人版で、口径（分子の集め方）は KPI 側と
 * 同じく `REQUIREMENT_DEFINITION_SET_ISSUE_TYPE_NAMES` の完全一致で切る。
 *
 * 判定キーが `issue_type_id` ではなく**種別名**なのは、pt 明細 API が引く
 * `kpi_develop_point_detail_table` が種別 ID を持たず種別名しか引き継がないため
 * （SSOT 側の JSDoc 参照）。「総合テスト作成」と前方一致する別種別（`総合テスト作成レビュー`）が
 * 実在するので、前方一致ではなく完全一致で当てること。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business メンバー当月pt の明細行を要件定義セット子票（業務フロー図作成/バリデーションリスト作成/機能一覧作成/要件定義レビュー/総合テスト作成）とそれ以外へ割り、pt 合計と占比を算出する
 * @graph-connects REQUIREMENT_DEFINITION_SET_ISSUE_TYPE_NAMES [references] 要件定義セットの課題種別名 SSOT
 */
import { REQUIREMENT_DEFINITION_SET_ISSUE_TYPE_NAMES } from '@cortex/domain-product-innovation-lab-dev-tree';

/**
 * 要件定義セットの課題種別名の集合（完全一致判定用）。
 * SSOT の配列をそのまま `includes` するより、明細行ごとの判定が O(1) になる。
 * @graph-connects none
 */
const REQUIREMENT_TYPE_NAME_SET: ReadonlySet<string> = new Set(
  REQUIREMENT_DEFINITION_SET_ISSUE_TYPE_NAMES,
);

/**
 * 当月pt の内訳で使う明細行 1 件分。完了明細（`developPoint`）と処理中明細（`point`）の
 * どちらもこの形へ寄せてから割る。
 * @graph-connects none
 */
export interface MemberPtRow {
  /** Backlog チケットキー */
  issueKey: string;
  /** 件名 */
  summary: string;
  /** 課題種別名（未設定なら null） */
  issueTypeName: string | null;
  /** 実績pt */
  point: number;
  /** 完了日（完了明細のみ。処理中は null） */
  completedDate: string | null;
  /** 母票（親チケット）のキー。明細モーダルを案件単位に畳むのに使う。引けなければ null */
  parentIssueKey: string | null;
  /** 母票の件名。引けなければ null */
  parentSummary: string | null;
}

/** 当月pt の内訳（pt 合計と占比）。 @graph-connects none */
export interface MemberPtBreakdown {
  /** 要件定義pt 合計（完了 + 処理中） */
  requirementPt: number;
  /** 開発pt 合計（要件定義セット以外。完了 + 処理中） */
  developPt: number;
  /** 当月pt 合計（= 要件定義pt + 開発pt） */
  totalPt: number;
  /** 要件定義pt の占比（%）。合計 0 なら 0 */
  requirementPercent: number;
  /** 開発pt の占比（%）。合計 0 なら 0 */
  developPercent: number;
  /** 要件定義セットの完了明細（元の並び順） */
  requirementCompletedRows: MemberPtRow[];
  /** 要件定義セットの処理中明細（元の並び順） */
  requirementWipRows: MemberPtRow[];
}

/** 種別名が要件定義セットの子票か（完全一致）。 @graph-connects none */
export function isRequirementDefinitionIssueType(issueTypeName: string | null): boolean {
  return issueTypeName !== null && REQUIREMENT_TYPE_NAME_SET.has(issueTypeName);
}

/**
 * pt 合計の丸め（小数第1位）。明細の pt を素直に足すと `12.299999999999999` のような
 * 浮動小数の端数が出て、占比の分母と表示値がわずかにずれる。
 * @graph-connects none
 */
function round1(value: number): number {
  return Math.round(value * 10) / 10;
}

/**
 * 占比（%、小数第1位）。合計 0（実績なし）のときは 0 を返し、バーは空のまま描く。
 * @graph-connects none
 */
function percentOf(part: number, total: number): number {
  if (total <= 0) return 0;
  return round1((part / total) * 100);
}

/**
 * 完了明細 + 処理中明細を要件定義pt / 開発pt へ割る。
 *
 * 口径は「当月pt」カードと同じ **当月の完了 + 処理中（現在スナップショット）**。処理中を
 * 外すと当月pt カードの大数と内訳バーの合計が一致せず、同じ行に並ぶ 2 枚のカードが
 * 違う数を出すことになる。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 当月の完了pt 明細と処理中pt 明細を要件定義セットとそれ以外へ割り、pt 合計・占比・要件定義明細を返す
 * @graph-connects isRequirementDefinitionIssueType [calls] 要件定義セット子票の判定
 */
export function buildMemberPtBreakdown(
  completedRows: readonly MemberPtRow[],
  wipRows: readonly MemberPtRow[],
): MemberPtBreakdown {
  const requirementCompletedRows = completedRows.filter((r) =>
    isRequirementDefinitionIssueType(r.issueTypeName),
  );
  const requirementWipRows = wipRows.filter((r) =>
    isRequirementDefinitionIssueType(r.issueTypeName),
  );

  const sum = (rows: readonly MemberPtRow[]): number => rows.reduce((acc, r) => acc + r.point, 0);
  const requirementPt = round1(sum(requirementCompletedRows) + sum(requirementWipRows));
  const totalPt = round1(sum(completedRows) + sum(wipRows));
  // 開発pt は「合計 − 要件定義pt」で出す。要件定義セット以外を別に足すと、丸めた 2 つの値の
  // 和が丸めた合計と 0.1 ずれることがあり、バーの幅が 100% に収まらなくなる。
  const developPt = round1(totalPt - requirementPt);

  return {
    requirementPt,
    developPt,
    totalPt,
    requirementPercent: percentOf(requirementPt, totalPt),
    developPercent: percentOf(developPt, totalPt),
    requirementCompletedRows,
    requirementWipRows,
  };
}
